import React, { useEffect, useRef } from 'react';
import PricingCTA from './PricingCTA';

const Archangel = () => {
  const heroVideoRef = useRef(null);
  const audioRef = useRef(null);
  const audioTimeoutRef = useRef(null); // Track the timeout

  // Handle audio switching when component mounts
  useEffect(() => {
    // Save original body style
    const originalStyle = window.getComputedStyle(document.body).overflow;

    // 1. First, stop any existing Archangel audio that might be playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // 2. Stop the theme audio
    if (window.orchThemeAudio) {
      window.orchThemeAudio.pause();
    }

    // 3. Stop any other audio elements
    document.querySelectorAll('audio').forEach(audio => {
      audio.pause();
    });

    // 4. Create only one instance of the Archangel audio with a timeout
    audioTimeoutRef.current = setTimeout(() => {
      // Create a single instance of the audio
      const archangelAudio = new Audio('/audio/archangel.mp3');
      archangelAudio.volume = 0.4;
      archangelAudio.currentTime = 65; // Start at 1:05

      // Store the reference
      audioRef.current = archangelAudio;

      // Play the audio once
      const playPromise = archangelAudio.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.error('Archangel audio play failed:', err);
        });
      }
    }, 300);

    // Disable scrolling
    document.body.style.overflow = 'hidden';

    // Clean up when component unmounts
    return () => {
      // Clear the timeout to prevent creating new audio
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }

      // Stop our custom audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = ''; // Completely unload
        audioRef.current = null;
      }

      // Restore original body style
      document.body.style.overflow = originalStyle;

      // Only restart the theme music if we're going back to the homepage
      if (window.location.pathname === '/' && window.orchThemeAudio) {
        const themePromise = window.orchThemeAudio.play();
        if (themePromise !== undefined) {
          themePromise.catch(e => console.log("Theme audio resume failed:", e));
        }
      }
    };
  }, []);

  return (
    <div className="absolute inset-0 w-full bg-black overflow-y-auto">
      {/* Hero Section */}
      <div className="h-screen relative">
        <div className="absolute inset-0 bg-black/60 z-10" />
        <video
          ref={heroVideoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{
            objectFit: "cover",
            objectPosition: "center center",
            // transform: "scale(0.75)" // Zoom out
          }}
          onError={(e) => console.error('Video error:', e)}
          onCanPlay={(e) => e.target.play().catch(err => console.error('Autoplay failed:', err))}
        >
          <source src="/videos/archeangel.mp4" type="video/mp4" />
        </video>

        <div className="relative z-20 container mx-auto px-4 pt-32">
          <h1 className="text-5xl md:text-7xl text-white font-light tracking-wider mb-6 drop-shadow-lg">
            Archangel
          </h1>
          <p className="text-xl md:text-2xl text-white/80 font-light tracking-wide max-w-2xl">
            A unified camera network <span className="font-medium text-white">solving major crimes</span> and <span className="font-medium text-white">defending against terror attacks</span> in San Francisco.
          </p>
        </div>

        {/* Scroll indicator positioned at bottom of hero */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
          <div className="animate-bounce flex flex-col items-center">
            <div className="bg-black/40 p-3 rounded-full border border-white/20 backdrop-blur-sm mb-2">
              <svg className="w-6 h-6 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
            <span className="text-xs text-white/70 tracking-wider">SCROLL</span>
          </div>
        </div>
      </div>



      {/* Main Content */}
      <div className="relative bg-black py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Bold Headline */}
            <div className="mb-20 text-center">
              <h2 className="text-3xl md:text-5xl text-white font-light tracking-wider mb-6">
                Providing city‑wide video evidence — already assisting San Francisco PD & Burlingame PD
              </h2>
              <div className="w-24 h-1 bg-red-600 mx-auto"></div>

              {/* Styled description with key points */}
              <div className="max-w-3xl mx-auto mt-8 mb-4 flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6">
                <div className="flex-1 bg-black/40 p-6 rounded-lg border border-white/10 backdrop-blur-sm">
                  <div className="flex items-start">
                    <div className="mr-4 mt-1">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <p className="text-white/90 text-md">
                      <span className="font-medium text-white">A public‑safety tool</span> built on a privately‑owned network of storefront cameras.
                    </p>
                  </div>
                </div>

                <div className="flex-1 bg-black/40 p-6 rounded-lg border border-white/10 backdrop-blur-sm">
                  <div className="flex items-start">
                    <div className="mr-4 mt-1">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-white/90 text-md">
                      Records major crimes in HD and delivers <span className="font-medium text-white">evidence to detectives</span>.
                    </p>
                  </div>
                </div>
              </div>
            </div>


            {/* Real Case Section - Redesigned to be more direct and impactful */}
            <div className="mb-20">
              <h3 className="text-2xl md:text-3xl text-white font-light tracking-wider mb-4">
                Key Evidence for Major Crimes
              </h3>

              {/* New introductory paragraph */}
              <div className="mb-6">
                <p className="text-lg text-white/90 font-light">
                  Within days of launch, Archangel recorded a stolen vehicle and captured two individuals inside it shortly before officers made the arrest.
                  The footage is now being used as key evidence in the case.
                </p>
              </div>

              <div className="bg-white/5 rounded-lg p-8 mb-8">
                {/* Video Evidence */}
                <div className="mb-12">
                  {/* YouTube Video Embed */}
                  <div className="relative pb-9 h-0 mb-6" style={{ paddingBottom: "56.25%" }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                      src="https://www.youtube.com/embed/CPu50EyYbRM"
                      title="Crime Evidence Footage"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen>
                    </iframe>
                  </div>

                  {/* Case Description - Simplified and merged with "How it Works" */}
                  <div className="bg-black/30 p-6 rounded-lg">
                    <h4 className="text-xl text-white font-light mb-4 border-b border-white/20 pb-2">Stolen Vehicle Recovery: How It Happened</h4>

                    <div className="flex flex-col space-y-6">
                      <div className="flex items-start">
                        <p className="text-white/90 space-y-4">
                          <span className="block mb-4">
                            A vehicle was reported stolen in Burlingame - 17 miles south of San Francisco.
                          </span>

                          <span className="block mb-4">
                            The next morning, we saw the report on our city crime map and checked nearby camera footage.
                          </span>

                          <span className="block mb-4">
                            The stolen car appeared across multiple cameras — driving through San Francisco, stopping in North Beach, and ending with police making the arrest on camera.
                          </span>

                          <span className="block mb-4">
                            We traced its path through the city, moment by moment. <i>It's like Strava for catching criminals.</i>
                          </span>

                          <span className="block">
                            Investigators later confirmed <strong> Archangel's footage is the only video evidence in the case and will be presented in court. </strong>
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>


                  {/* Law Enforcement Partner Logos - Kept and improved */}
                  <div className="mt-6 flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8 bg-black/40 p-6 rounded-lg">
                    <p className="text-white/70 text-sm font-light">Case evidence used by:</p>
                    <div className="flex items-center space-x-10">
                      <div className="flex flex-col items-center">
                        <div className="h-16 w-16 mb-2">
                          <img
                            src="/images/sfpd.png"
                            alt="SFPD"
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <span className="text-white/90 text-xs tracking-wide">San Francisco Police Department</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="h-16 w-16 mb-2">
                          <img
                            src="/images/bpd.jpg"
                            alt="Burlingame PD"
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <span className="text-white/90 text-xs tracking-wide">Burlingame Police Department</span>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>


            {/* CTA Section */}
            <div className="text-center mb-0">
              <h3 className="text-2xl md:text-3xl text-white font-light tracking-wider mb-1">
                Unsolved major crime in SF?
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Add PricingCTA before closing divs */}
      <PricingCTA className="-mt-12 mb-8" />
    </div>
  );
};

export default Archangel; 